/**
 * `tracks` — the host-agnostic **track-editor model**: the pure seam between a combatant's tracked
 * values and the `set_hp` / `set_heat` commands the encounter room dispatches, following the same
 * discipline `deploy.ts` does for `add_combatant`. {@link trackViews} reads a combatant's editable
 * tracks with their `[0, max]` bounds (the affordance mirror of the reducer's clamp) and {@link
 * setTrackCommand} builds the absolute-set command to dispatch.
 *
 * **Only HP and heat are manually settable.** Structure and stress are *consequence* tracks
 * driven by resolution steps (`structure_delta` / `stress_delta`) — the encounter reducer
 * exposes no `set_structure` / `set_stress` command, so they are display-only and not edited
 * here. Heat is present only for kinds with a heat track (`base.maxHeat !== undefined`); the
 * reducer rejects `set_heat` on a heatless combatant, and `trackViews` mirrors that by omitting it.
 */
import type { Combatant, CombatantId, SetHeatCommand, SetHpCommand } from "@lancer-vtt/domain";

/** The manually-settable tracks (HP always; heat only for kinds with a heat track). */
export type TrackKind = "hp" | "heat";

/** One editable track of a combatant — label + current value + its `[0, max]` bound. */
export type TrackView = {
  kind: TrackKind;
  label: string;
  value: number;
  /** The reducer's clamp ceiling — the stepper disables at `[0, max]` as an affordance. */
  max: number;
};

/**
 * The editable tracks a combatant exposes, in display order: HP always, then heat for
 * kinds with a heat track. Pure — a stable mapping from a combatant to its track views.
 */
export function trackViews(c: Combatant): TrackView[] {
  const views: TrackView[] = [{ kind: "hp", label: "HP", value: c.current.hp, max: c.base.maxHp }];
  if (c.base.maxHeat !== undefined) {
    views.push({ kind: "heat", label: "Heat", value: c.current.heat ?? 0, max: c.base.maxHeat });
  }
  return views;
}

export type SetTrackArgs = {
  actorId: string;
  combatantId: CombatantId;
  kind: TrackKind;
  /** The absolute value to set (the reducer clamps a slipped-through out-of-bounds value). */
  value: number;
};

/**
 * Build the absolute-set command for a track. The reducer clamps into `[0, max]`, so the
 * host commits the raw entered value and the reconcile snaps the display to the clamped
 * truth — affordances here, enforcement there.
 */
export function setTrackCommand(args: SetTrackArgs): SetHpCommand | SetHeatCommand {
  const { actorId, combatantId, kind, value } = args;
  return kind === "hp"
    ? { type: "set_hp", actorId, combatantId, hp: value }
    : { type: "set_heat", actorId, combatantId, heat: value };
}
